import { z } from 'zod';

import type { UserNode } from '../../gql/graphql';
import { OVERALL_RATING_VALUES, POSTING_SOURCE_NAMES } from '../constants';

// Basic filter schemas
const talentDiscoveryAiSearchFilter = z.object({
  name: z.string(),
  relevantPromptText: z.string(),
});

/**
 * Whether the user asked to KEEP or LEAVE OUT the population an item names
 * ("Google except Apple" -> Google include, Apple exclude), classified by the
 * model in any language. Required on the items the model fills
 * (`polarityForOpenAI`). Optional on the validating schema, but only the
 * compact extraction parses the model's answer with that schema: there an
 * absent value means include, since failing the parse would cost every other
 * filter in the prompt. The two-call extraction validates its merged answer against
 * the model-facing schema, which rejects it.
 */
export const FILTER_POLARITIES = ['include', 'exclude'] as const;

export type FilterPolarity = (typeof FILTER_POLARITIES)[number];

const filterPolaritySchema = z.enum(FILTER_POLARITIES);

/**
 * A new enum per object the model fills: one shared instance renders as a
 * `$ref` at every use after the first, which is longer than the enum inline.
 */
const polarityForOpenAI = () => ({ polarity: z.enum(FILTER_POLARITIES) });

const polarityTolerated = { polarity: filterPolaritySchema.optional() };

// Base schema without optional fields (for OpenAI)
const talentDiscoveryAiSearchPreviousCurrentFilterBase =
  talentDiscoveryAiSearchFilter.extend({
    isCurrent: z.boolean(),
  });

/**
 * The granularity the USER asked for, classified by the model rather than by
 * string-matching their words: "Hartford County", "comté de Hartford" and a
 * misspelled "Swanly" all have to land on a level, and any code that tries to
 * read that out of the raw text is brittle to synonyms, languages and typos.
 *
 * Optional on purpose — the model omits it when it cannot tell what kind of
 * place was named, and consumers must treat "absent" as "unknown", never as a
 * default level.
 */
const GEO_ASKED_LEVELS = [
  'region',
  'country',
  'state',
  'county',
  'metro_area',
  'city',
] as const;

export type GeoAskedLevel = (typeof GEO_ASKED_LEVELS)[number];

const geoAskedLevelSchema = z.enum(GEO_ASKED_LEVELS);

/**
 * Geography items carry the asked granularity; every other filter family uses
 * the shared base, so the field cannot leak into company/school extraction.
 */
const talentDiscoveryAiSearchGeographyFilterBase =
  talentDiscoveryAiSearchPreviousCurrentFilterBase.extend({
    name: z
      .string()
      .describe(
        'The place alone in the user’s wording, without its broader qualifier. North America stays North America; Portland, Maine has name Portland.'
      ),
    level: geoAskedLevelSchema
      .nullable()
      .describe(
        'Requested geographic granularity. North America is region; Ontario is state. Null only when unknown.'
      ),
    /**
     * The broader place that disambiguates `name`, as the model knows it —
     * "Portland" in "Maine, United States", "München" in "Bavaria, Germany" —
     * or null when the name is unambiguous. The resolver composes the Location
     * Mapper query from the two fields.
     */
    // Explicit union is intentional: this SDK renders string().nullable() as
    // OpenAPI nullable:true, which does not allow JSON null in JSON Schema.
    containedIn: z
      .union([z.string(), z.null()])
      .describe(
        'A real broader place name used to disambiguate this place (Portland -> Maine, United States), or JSON null for unqualified unambiguous places (North America, Germany). Never a string "null", empty text, punctuation, or JSON syntax.'
      ),
  });

/**
 * How specific the role phrase was, classified by the model rather than by
 * string-matching, as `GEO_ASKED_LEVELS` is. `generic_job_noun` ("an open
 * role", "un poste ouvert") names a job without naming any kind of work and is
 * not a role filter; matched as text, "role" is a substring of pet-ROLE-um.
 * `breakdown_axis` ("by role", "for which roles") names the dimension the
 * answer is split by, not a population. `seniority_rank` ("managers", "VPs")
 * names a career level, which is the seniority filter's job. Neither is a role.
 */
const ROLE_SPECIFICITY_LEVELS = [
  'named_occupation',
  'named_function',
  'generic_job_noun',
  'breakdown_axis',
  'seniority_rank',
] as const;

const roleSpecificitySchema = z.enum(ROLE_SPECIFICITY_LEVELS);

/**
 * Its own base, not the shared one, so the field cannot leak into other
 * families. Title items carry it too: one filed without title intent becomes a
 * role.
 */
const talentDiscoveryAiSearchRoleFilterBase =
  talentDiscoveryAiSearchPreviousCurrentFilterBase.extend({
    specificity: roleSpecificitySchema,
  });

/**
 * `titleIntentText`: the prompt words asking about a job title, or null for a
 * bare occupation (dash-meta then files it as a role, keeping `specificity`).
 * Declared first so the model commits to the evidence before naming the title.
 */
const talentDiscoveryAiSearchTitleFilterForOpenAI = z.object({
  titleIntentText: z.string().nullable(),
  name: z.string(),
  relevantPromptText: z.string(),
  isCurrent: z.boolean(),
  specificity: roleSpecificitySchema,
});

/**
 * Which filter claims a keywords item, classified by the model: `person_signal`
 * when none does, `occupation` when role or titles does, otherwise that
 * filter's key. Optional on the response schema: backend fallbacks append
 * unclassified keyword items after extraction.
 */
const KEYWORD_CLAIMING_FILTERS = [
  'company',
  'competitors_of',
  'industry',
  'geography',
  'school',
  'highest_degree',
  'seniority',
  'salary_total',
  'years_of_experience',
  'graduation_year',
  'name',
  'gender',
  'ethnicity',
  'prestige',
  'remote_suitability',
  'flight_risk',
  'employment_relationship',
  'work_schedule',
  'skill',
  'activity',
  'overall_rating',
  'posting_source',
  'date_range',
] as const;

const KEYWORD_KINDS = [
  'person_signal',
  'occupation',
  ...KEYWORD_CLAIMING_FILTERS,
] as const;

export type KeywordKind = (typeof KEYWORD_KINDS)[number];

const keywordKindSchema = z.enum(KEYWORD_KINDS);

const talentDiscoveryAiSearchKeywordFilterBase =
  talentDiscoveryAiSearchFilter.extend({
    kind: keywordKindSchema,
  });

const talentDiscoveryAiSearchKeywordFilter =
  talentDiscoveryAiSearchFilter.extend({
    kind: keywordKindSchema.optional(),
  });

// Full schema with optional backend-added fields (for response type)
const talentDiscoveryAiSearchPreviousCurrentFilter =
  talentDiscoveryAiSearchPreviousCurrentFilterBase.extend({
    // Optional: frontend-ready filter IDs (added by backend after OpenAI response)
    id: z.string().optional(),
    levelId: z.string().optional(),
  });

// Skill filter schema with optional backend-added fields (for response type)
const talentDiscoveryAiSearchSkillFilter = talentDiscoveryAiSearchFilter.extend(
  {
    // Optional: frontend-ready filter IDs (added by backend after OpenAI response)
    id: z.string().optional(),
    levelId: z.string().optional(),
  }
);

/**
 * How a skill combines with the skill listed just before it: "and" when the
 * person must have both, "or" when either is enough (the first item's value is
 * ignored), decided by the model in any language. Code builds the AND-of-OR
 * groups from it: the compact extraction does not reliably express AND in the
 * nested array shape, but does fill a per-item enum. Required on the
 * `ForOpenAI` twin. Optional on the validating schema, which only the compact
 * extraction parses the model's answer with; absent there starts a new group.
 */
const SKILL_JOINS = ['and', 'or'] as const;

export type SkillJoin = (typeof SKILL_JOINS)[number];

const skillJoinSchema = z.enum(SKILL_JOINS);

// Built on the bare filter: `id` and `levelId` are the resolver's to fill, and
// a strict schema would make the model invent them.
const talentDiscoveryAiSearchSkillFilterForOpenAI =
  talentDiscoveryAiSearchFilter.extend({ withPrevious: skillJoinSchema });

// Activity filter schema with optional backend-added fields (for response type)
const talentDiscoveryAiSearchActivityFilter =
  talentDiscoveryAiSearchFilter.extend({
    // Optional: frontend-ready filter IDs (added by backend after OpenAI response)
    id: z.string().optional(),
    levelId: z.string().optional(),
  });

// School filter schema with optional backend-added fields (for response type)
const talentDiscoveryAiSearchSchoolFilter =
  talentDiscoveryAiSearchFilter.extend({
    // Optional: frontend-ready filter IDs (added by backend after OpenAI response)
    id: z.string().optional(),
    levelId: z.string().optional(),
  });

/**
 * Which side(s) of a numeric range the user constrained — "over 200k" is
 * at_least, "no more than 5 years" at_most, "exactly 7" exact, "5 to 10"
 * between — classified by the model in any language. Required on the
 * `…ForOpenAI` twin the model fills. Optional on the validating schema, which
 * only the compact extraction parses the model's answer with (absent there:
 * trust the values as given). It cannot be one optional field: this object is
 * reused, so it renders as a `$ref`, where `.optional()` and `.default()` both
 * produce JSON schema OpenAI rejects (see openai-response-schemas.spec.ts).
 */
const RANGE_COMPARISONS = ['exact', 'at_least', 'at_most', 'between'] as const;

export type RangeComparison = (typeof RANGE_COMPARISONS)[number];

export const rangeComparisonSchema = z.enum(RANGE_COMPARISONS);

const talentDiscoveryAiSearchRangeFilter = z.object({
  start_value: z.number().nullable(),
  end_value: z.number().nullable(),
  relevantPromptText: z.string(),
  isCurrent: z.boolean(),
  comparison: rangeComparisonSchema.optional(),
});

const talentDiscoveryAiSearchRangeFilterForOpenAI =
  talentDiscoveryAiSearchRangeFilter.extend({
    // The installed SDK renders .nullable() as the OpenAPI-only nullable
    // extension. A union permits actual JSON null in the strict wire schema.
    start_value: z
      .union([z.number(), z.null()])
      .describe(
        'Lower bound. For at_least use the stated value; for at_most use JSON null. For exact use the same value as end_value; for between use the lower value. Never invent a bound or use 0 for an unspecified bound.'
      ),
    end_value: z
      .union([z.number(), z.null()])
      .describe(
        'Upper bound. For at_most use the stated value; for at_least use JSON null. For exact use the same value as start_value; for between use the upper value. Never invent a bound or use 0 for an unspecified bound.'
      ),
    comparison: rangeComparisonSchema.describe(
      'The numeric constraint by meaning: at_least is a floor (including no earlier than); at_most is a ceiling (including no later than); exact is one value without comparison; between has two stated bounds. A negated comparison does not itself exclude people: exclusion is expressed separately by polarity.'
    ),
  });

// NEW: date range filter (ISO date strings, e.g. "2025-01-31")
const talentDiscoveryAiSearchDateRangeFilter = z.object({
  start_value: z.string().nullable(),
  end_value: z.string().nullable(),
  relevantPromptText: z.string(),
  isCurrent: z.boolean(),
  /**
   * Relative-window preset token ('last-year', 'last-6-months', …) the model
   * emits INSTEAD of guessing absolute dates for named relative windows — the
   * model has no reliable notion of today. dash-meta's DateRangeResolver
   * resolves it against the data index's latest date (filling
   * start_value/end_value), and KEEPS the token in the response so the
   * dashboard's apply path can re-resolve it against the trial-lag-aware max.
   * Loosely typed on purpose: an unknown token degrades to whatever dates are
   * present rather than failing validation. See
   * `@revelio/core/global-ai-assistant/date-range-presets`.
   */
  preset: z.string().nullable().optional(),
});

/** An employee-review star rating, 1-5. */
const talentDiscoveryAiSearchOverallRatingFilterBase = z.object({
  name: z.string(),
  relevantPromptText: z.string(),
});

/**
 * A posting source the model read from the prompt — a job board or the
 * company's own website, never the employer being asked about.
 *
 * `name` is a plain string HERE, on the VALIDATING side, deliberately: a
 * `safeParse` failure on the response throws and loses the whole extraction,
 * so an unlisted board has to degrade into `unknownFilters` rather than cost
 * the user every other filter they asked for. The closed set is enforced on
 * the way OUT instead — see the `…ForOpenAI` twins below.
 */
const talentDiscoveryAiSearchPostingSourceFilterBase = z.object({
  name: z.string(),
  relevantPromptText: z.string(),
});

/**
 * The same two dimensions as the model is CONSTRAINED to emit them.
 *
 * A `z.enum` on the REQUEST schema is how a closed set becomes the model's own
 * choice returned structured, which is what
 * `.ai-rules/global-ai-assistant-routing.md` asks for: structured outputs
 * enforce it at the provider, so an unlisted board is unemittable rather than
 * matched back from the user's words afterwards. Held apart from the
 * validating schemas above so that tightening the ASK cannot turn one
 * hallucinated word into a failed turn.
 */
const talentDiscoveryAiSearchPostingSourceFilterForOpenAI = z.object({
  name: z.enum(POSTING_SOURCE_NAMES),
  relevantPromptText: z.string(),
});

const talentDiscoveryAiSearchOverallRatingFilterForOpenAI = z.object({
  name: z.enum(OVERALL_RATING_VALUES),
  relevantPromptText: z.string(),
});

/**
 * `id` is the dashboard option value the RESOLVER matched the name to. It is on
 * the internal schemas only, NEVER the OpenAI-facing ones: every consumer reads
 * a present `id` as "the resolver matched this", so a model-invented one would
 * survive `safeParse` as a match that never happened — a fail-open on the one
 * field whose purpose is to fail closed.
 */
const talentDiscoveryAiSearchOverallRatingFilter =
  talentDiscoveryAiSearchOverallRatingFilterBase.extend({
    id: z.string().optional(),
  });

const talentDiscoveryAiSearchPostingSourceFilter =
  talentDiscoveryAiSearchPostingSourceFilterBase.extend({
    id: z.string().optional(),
  });

const talentDiscoveryAiSearchUnknownFilter =
  talentDiscoveryAiSearchFilter.extend({
    explanation: z.string(),
  });

// Predefined values
const talentDiscoveryAiSearchEducationValues = z.enum([
  'High School',
  'Associate',
  'Bachelor',
  'MBA',
  'Master',
  'Doctor',
]);

const talentDiscoveryAiSearchEthnicityValues = z.enum([
  'White',
  'Asian',
  'Hispanic',
  'Black',
  'Native',
  'Multiple',
]);

const talentDiscoveryAiSearchRiskPrestigeSuitabilityValues = z.enum([
  'Very Low',
  'Low',
  'Medium',
  'High',
  'Very High',
]);

const talentDiscoveryAiSearchEmploymentRelationshipValues = z.enum([
  'Permanent',
  'Contract',
  'Internship',
  'Self-employed',
  'Seasonal',
]);

const talentDiscoveryAiSearchWorkScheduleValues = z.enum([
  'Full-time',
  'Part-time',
]);

const talentDiscoveryAiSearchGenderValues = z.enum(['Male', 'Female']);

export const SENIORITY_LEVELS = [
  'Entry Level',
  'Junior',
  'Associate',
  'Manager',
  'Director',
  'Executive',
  'Senior Executive',
] as const;

const talentDiscoveryAiSearchSeniorityValues = z.enum(SENIORITY_LEVELS);

/**
 * Whether the user's words name the seniority level the model chose, or name a
 * rank the list does not have ("senior managers") so the level is only the
 * closest one. Classified by the model: the enum on `name` forces SOME level,
 * and nothing in the words alone tells code whether that level is the one asked
 * for.
 */
const SENIORITY_LEVEL_MATCHES = ['named', 'nearest'] as const;

const seniorityLevelMatchSchema = z.enum(SENIORITY_LEVEL_MATCHES);

// Filter schemas with predefined values
const talentDiscoveryAiSearchEducationFilter =
  talentDiscoveryAiSearchFilter.extend({
    name: talentDiscoveryAiSearchEducationValues,
  });

const talentDiscoveryAiSearchEthnicityFilter =
  talentDiscoveryAiSearchFilter.extend({
    name: talentDiscoveryAiSearchEthnicityValues,
  });

const talentDiscoveryAiSearchRiskPrestigeSuitabilityFilter =
  talentDiscoveryAiSearchPreviousCurrentFilterBase.extend({
    name: talentDiscoveryAiSearchRiskPrestigeSuitabilityValues,
  });

const talentDiscoveryAiSearchEmploymentRelationshipFilter =
  talentDiscoveryAiSearchPreviousCurrentFilterBase.extend({
    name: talentDiscoveryAiSearchEmploymentRelationshipValues,
  });

const talentDiscoveryAiSearchWorkScheduleFilter =
  talentDiscoveryAiSearchPreviousCurrentFilterBase.extend({
    name: talentDiscoveryAiSearchWorkScheduleValues,
  });

const talentDiscoveryAiSearchGenderFilter =
  talentDiscoveryAiSearchFilter.extend({
    name: talentDiscoveryAiSearchGenderValues,
  });

const talentDiscoveryAiSearchSeniorityFilter =
  talentDiscoveryAiSearchPreviousCurrentFilterBase.extend({
    name: talentDiscoveryAiSearchSeniorityValues,
    // Optional here (required for the model): resolved items written back
    // carry no label, and absent keeps the filter.
    levelMatch: seniorityLevelMatchSchema.optional(),
  });

const talentDiscoveryAiSearchSeniorityFilterForOpenAI =
  talentDiscoveryAiSearchSeniorityFilter.extend({
    levelMatch: seniorityLevelMatchSchema,
  });

// Main filter schema
const talentDiscoveryAiSearchFilters = z
  .object({
    company: z
      .array(
        talentDiscoveryAiSearchPreviousCurrentFilter.extend(polarityTolerated)
      )
      .nullable(),
    competitors_of: z
      .array(talentDiscoveryAiSearchFilter.extend(polarityTolerated))
      .nullable(),
    industry: z
      .array(
        talentDiscoveryAiSearchPreviousCurrentFilter.extend(polarityTolerated)
      )
      .nullable(),
    highest_degree: z
      .array(talentDiscoveryAiSearchEducationFilter.extend(polarityTolerated))
      .nullable(),
    ethnicity: z
      .array(talentDiscoveryAiSearchEthnicityFilter.extend(polarityTolerated))
      .nullable(),
    flight_risk: z
      .array(
        talentDiscoveryAiSearchRiskPrestigeSuitabilityFilter.extend(
          polarityTolerated
        )
      )
      .nullable(),
    gender: z
      .array(talentDiscoveryAiSearchGenderFilter.extend(polarityTolerated))
      .nullable(),
    geography: z
      .array(
        talentDiscoveryAiSearchGeographyFilterBase.extend({
          // Tolerated as ABSENT when parsing, though the OpenAI schema above
          // requires it: a missing level means "the model did not classify
          // this ask", which degrades one disclosure. Requiring it here would
          // instead fail the whole parse and cost the user every filter in the
          // prompt — far too much to stake on a nicety.
          level: geoAskedLevelSchema.nullable().optional(),
          // Tolerated as absent for the same reason as `level`.
          containedIn: z.string().nullable().optional(),
          // Backend-added after resolution, exactly like every other tree filter.
          id: z.string().optional(),
          levelId: z.string().optional(),
          ...polarityTolerated,
        })
      )
      .nullable(),
    name: talentDiscoveryAiSearchFilter.extend(polarityTolerated).nullable(),
    prestige: z
      .array(
        talentDiscoveryAiSearchRiskPrestigeSuitabilityFilter.extend(
          polarityTolerated
        )
      )
      .nullable(),
    remote_suitability: z
      .array(
        talentDiscoveryAiSearchRiskPrestigeSuitabilityFilter.extend(
          polarityTolerated
        )
      )
      .nullable(),
    employment_relationship: z
      .array(
        talentDiscoveryAiSearchEmploymentRelationshipFilter.extend(
          polarityTolerated
        )
      )
      .nullable(),
    work_schedule: z
      .array(
        talentDiscoveryAiSearchWorkScheduleFilter.extend(polarityTolerated)
      )
      .nullable(),
    role: z
      .array(
        // `specificity` is declared here too because model output is parsed
        // through this schema and zod drops undeclared keys, which would hide
        // the label from `dropRoleItemsThatNameNoRole`. Optional because this schema
        // also types the resolved role items written back, which carry no label.
        talentDiscoveryAiSearchPreviousCurrentFilter.extend({
          ...polarityTolerated,
          specificity: roleSpecificitySchema.optional(),
        })
      )
      .nullable(),
    school: z
      .array(talentDiscoveryAiSearchSchoolFilter.extend(polarityTolerated))
      .nullable(),
    seniority: z
      .array(talentDiscoveryAiSearchSeniorityFilter.extend(polarityTolerated))
      .nullable(),
    skill: z
      .array(
        z.array(
          talentDiscoveryAiSearchSkillFilter.extend({
            ...polarityTolerated,
            withPrevious: skillJoinSchema.optional(),
          })
        )
      )
      .nullable(),
    activity: z
      .array(
        z.array(talentDiscoveryAiSearchActivityFilter.extend(polarityTolerated))
      )
      .nullable(),
    keywords: z
      .array(
        z.array(talentDiscoveryAiSearchKeywordFilter.extend(polarityTolerated))
      )
      .nullable(),
    salary_total: z
      .array(talentDiscoveryAiSearchRangeFilter.extend(polarityTolerated))
      .nullable(),
    titles: z
      .array(
        z.array(
          talentDiscoveryAiSearchPreviousCurrentFilter.extend({
            ...polarityTolerated,
            // Optional here (required for the model): absent keeps the title.
            titleIntentText: z.string().nullable().optional(),
            specificity: roleSpecificitySchema.optional(),
          })
        )
      )
      .nullable(),
    graduation_year: talentDiscoveryAiSearchRangeFilter
      .extend(polarityTolerated)
      .nullable(),
    years_of_experience: talentDiscoveryAiSearchRangeFilter
      .extend(polarityTolerated)
      .nullable(),

    // NEW FILTERS
    date_range: talentDiscoveryAiSearchDateRangeFilter.nullable(),
    overall_rating: z
      .array(talentDiscoveryAiSearchOverallRatingFilter)
      .nullable(),
    posting_source: z
      .array(talentDiscoveryAiSearchPostingSourceFilter)
      .nullable(),
  })
  .required();

// One instance for each item shape several filters share, so the strict
// schema renders every use after the first as a `$ref` instead of a copy.
const previousCurrentItemForOpenAI =
  talentDiscoveryAiSearchPreviousCurrentFilterBase.extend(polarityForOpenAI());
const plainItemForOpenAI = talentDiscoveryAiSearchFilter.extend(
  polarityForOpenAI()
);
const riskPrestigeSuitabilityItemForOpenAI =
  talentDiscoveryAiSearchRiskPrestigeSuitabilityFilter.extend(
    polarityForOpenAI()
  );
// Range filters are NOT shared: a shared item renders years_of_experience and
// graduation_year as a `$ref` into salary_total's item, which the model reads
// as a salary shape and then leaves the user's range out.
const rangeItemForOpenAI = () =>
  talentDiscoveryAiSearchRangeFilterForOpenAI.extend(polarityForOpenAI());

// Main response schema
// Schema for OpenAI (without optional backend-added fields)
const talentDiscoveryAiSearchFiltersForOpenAI = z
  .object({
    company: z.array(previousCurrentItemForOpenAI).nullable(),
    competitors_of: z.array(plainItemForOpenAI).nullable(),
    industry: z.array(previousCurrentItemForOpenAI).nullable(),
    highest_degree: z
      .array(talentDiscoveryAiSearchEducationFilter.extend(polarityForOpenAI()))
      .nullable(),
    ethnicity: z
      .array(talentDiscoveryAiSearchEthnicityFilter.extend(polarityForOpenAI()))
      .nullable(),
    flight_risk: z.array(riskPrestigeSuitabilityItemForOpenAI).nullable(),
    gender: z
      .array(talentDiscoveryAiSearchGenderFilter.extend(polarityForOpenAI()))
      .nullable(),
    geography: z
      .array(
        talentDiscoveryAiSearchGeographyFilterBase.extend(polarityForOpenAI())
      )
      .nullable(),
    name: plainItemForOpenAI.nullable(),
    prestige: z.array(riskPrestigeSuitabilityItemForOpenAI).nullable(),
    remote_suitability: z
      .array(riskPrestigeSuitabilityItemForOpenAI)
      .nullable(),
    employment_relationship: z
      .array(
        talentDiscoveryAiSearchEmploymentRelationshipFilter.extend(
          polarityForOpenAI()
        )
      )
      .nullable(),
    work_schedule: z
      .array(
        talentDiscoveryAiSearchWorkScheduleFilter.extend(polarityForOpenAI())
      )
      .nullable(),
    role: z
      .array(talentDiscoveryAiSearchRoleFilterBase.extend(polarityForOpenAI()))
      .nullable(),
    school: z
      .array(talentDiscoveryAiSearchSchoolFilter.extend(polarityForOpenAI()))
      .nullable(),
    seniority: z
      .array(
        talentDiscoveryAiSearchSeniorityFilterForOpenAI.extend(
          polarityForOpenAI()
        )
      )
      .nullable(),
    skill: z
      .array(
        z.array(
          talentDiscoveryAiSearchSkillFilterForOpenAI.extend(
            polarityForOpenAI()
          )
        )
      )
      .nullable(),
    activity: z
      .array(
        z.array(
          talentDiscoveryAiSearchActivityFilter.extend(polarityForOpenAI())
        )
      )
      .nullable(),
    keywords: z
      .array(
        z.array(
          talentDiscoveryAiSearchKeywordFilterBase.extend(polarityForOpenAI())
        )
      )
      .nullable(),
    salary_total: z.array(rangeItemForOpenAI()).nullable(),
    titles: z
      .array(
        z.array(
          talentDiscoveryAiSearchTitleFilterForOpenAI.extend(
            polarityForOpenAI()
          )
        )
      )
      .nullable(),
    graduation_year: rangeItemForOpenAI().nullable(),
    years_of_experience: rangeItemForOpenAI().nullable(),

    // NEW FILTERS
    date_range: talentDiscoveryAiSearchDateRangeFilter.nullable(),
    overall_rating: z
      .array(talentDiscoveryAiSearchOverallRatingFilterForOpenAI)
      .nullable(),
    posting_source: z
      .array(talentDiscoveryAiSearchPostingSourceFilterForOpenAI)
      .nullable(),
  })
  .required();

export const talentDiscoveryAiSearchResponseSchemaV2ForOpenAI = z.object({
  filters: talentDiscoveryAiSearchFiltersForOpenAI,
  unknownFilters: z.array(talentDiscoveryAiSearchUnknownFilter),
});

export const talentDiscoveryAiSearchResponseSchemaV2 = z.object({
  filters: talentDiscoveryAiSearchFilters,
  unknownFilters: z.array(talentDiscoveryAiSearchUnknownFilter),
});

// Type inference
export type TalentDiscoveryAiSearchFiltersType = z.infer<
  typeof talentDiscoveryAiSearchFilters
>;

// V2 format response type using UserNode expression tree (final API response format)
export type TalentDiscoveryAiFilterSearchResponseV2 = {
  filters: {
    expr: UserNode;
    roles_tax: string;
    skills_tax: number;
    roles_tax_id?: number;
    skills_tax_id?: number;
    // The activity tree the activity nodes were resolved under (the account's,
    // or one derived from a named company when the preference is adaptive),
    // stated like the role and skill trees so nothing assumes the default.
    activities_tax?: number;
    activities_tax_id?: number;
    geo_tax_id?: number;
    schoolNames?: Array<{
      rsid: string;
      name: string;
    }>;
    companyNames?: Array<{
      rcid: number;
      name: string;
      // RICS ancestry from the company mapper, so the dashboard can build the
      // path-encoded company id (`${rics_k10}_${rics_k400}_${rcid}`) and resolve
      // the adaptive role/skill taxonomy natively — same as a company-search pick.
      rics_k10?: number | null;
      rics_k50?: number | null;
      rics_k400?: number | null;
      // The mapper record's k400 industry name and headquarters location, each
      // present only when the record has it. The assistant states them from here.
      industry?: string;
      hq_city?: string;
      hq_state?: string;
      hq_country?: string;
    }>;
    // Resolved role/skill nodes, mirroring companyNames/schoolNames: the
    // taxonomy tree these resolved against lives server-side, so the human
    // name is only correct-by-construction here. The FE cannot look this up
    // itself — at digest time the FilterProvider may still be on the
    // pre-pairing taxonomy, so a client-side lookup can return a label from
    // the WRONG tree (see transformValue.ts). `id`/`level` are the
    // frontend-ready selection-list coordinates (e.g. id: "role_k150.123",
    // level: "role_k150"); `taxonomy_id` is the numeric taxonomy the node
    // was resolved under (mirrors roles_tax_id/skills_tax_id above).
    roleNames?: Array<{
      id: string;
      level: string;
      taxonomy_id: number;
      name: string;
    }>;
    skillNames?: Array<{
      id: string;
      level: string;
      taxonomy_id: number;
      name: string;
    }>;
    activityNames?: Array<{
      id: string;
      level: string;
      taxonomy_id: number;
      name: string;
    }>;
    /**
     * The companies a "competitors of X" anchor expanded to, closest peer
     * first, all of them also in `companyNames`. A read covers only the
     * closest few of a peer set, so it needs the order the peers came in.
     */
    competitorPeerRcids?: number[];
    /** Resolved geography nodes, mirroring roleNames: labels/levels are only
     * correct-by-construction under the taxonomy resolution used; the FE's
     * FilterProvider may be on a different tree at digest time. */
    geographyNames?: Array<{
      id: string;
      level: string;
      taxonomy_id: number;
      name: string;
    }>;
  };
  unknownFilters: z.infer<typeof talentDiscoveryAiSearchUnknownFilter>[];
  relevantPromptText: {
    valid: string[];
    invalid: string[];
  };
  /** Present when the ask matched materially better in the OTHER geography
   * taxonomy; the applied filters above are the best fit in the user's own.
   * Drives the coarsening disclosure + inline switch offer. */
  geographyTaxonomyMismatch?: {
    otherTaxonomyId: number;
    /** How to NAME that taxonomy to the user, e.g. "State & County". */
    otherTaxonomyLabel: string;
    items: Array<{
      asked: string;
      /** Display name of what the OTHER taxonomy matched it to. */
      matchedName: string;
      /** That match's level tag on the other taxonomy, e.g. 'county'. */
      level: string;
      /**
       * What the ask was applied AS in the user's OWN taxonomy — the actual
       * filter. The resolver has always sent it; this type just did not say so,
       * which is how a consumer came to narrate `matchedName` as the place read.
       */
      appliedAs?: Array<{ name: string; level: string }>;
    }>;
  };
  // Non-TD filters: These are not included in the TD expression tree (expr)
  overall_rating?: z.infer<typeof talentDiscoveryAiSearchOverallRatingFilter>[];
  posting_source?: z.infer<typeof talentDiscoveryAiSearchPostingSourceFilter>[];
  date_range?: z.infer<typeof talentDiscoveryAiSearchDateRangeFilter>;
};
